export const OAUTH_BROKER_ROUTE = '/oauth-authorize';

const OAUTH_BROKER_STORAGE_KEY = 'sf.oauthBrokerContinuation.v1';
const OAUTH_BROKER_TERMINAL_KEY = 'sf.oauthBrokerContinuation.terminal.v1';
export const OAUTH_BROKER_MAX_AGE_MS = 10 * 60 * 1000;

type OAuthBrokerPhase = 'pending_login' | 'continuing';

const VALID_OAUTH_BROKER_PHASES: readonly OAuthBrokerPhase[] = ['pending_login', 'continuing'];

type OAuthBrokerRecord = {
    version: 1;
    tx: string;
    phase: OAuthBrokerPhase;
    createdAtMs: number;
};

const TERMINAL_REASONS = [
    'disabled',
    'invalid_request',
    'invalid_transaction',
    'malformed_response',
    'network_error',
    'organization_mismatch',
    'stale',
    'state_mismatch',
    'storage_unavailable',
    'unauthenticated',
    'unknown',
] as const;

export type OAuthBrokerTerminalReason = typeof TERMINAL_REASONS[number];

export type OAuthBrokerIngressResult =
    | 'captured'
    | 'invalid'
    | 'not_broker'
    | 'resumed'
    | 'storage_unavailable';

export type OAuthBrokerInspection = {
    active: boolean;
    owned: boolean;
    terminalReason: OAuthBrokerTerminalReason | null;
};

type Clock = () => number;

type BrowserForIngress = {
    history: Pick<History, 'replaceState'>;
    location: Pick<Location, 'hash' | 'origin' | 'pathname' | 'search'>;
    sessionStorage: Storage;
};

function isNonEmptyTx(tx: unknown): tx is string {
    return typeof tx === 'string' && tx.trim().length > 0;
}

function isRecord(value: unknown): value is OAuthBrokerRecord {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const record = value as Partial<OAuthBrokerRecord>;
    return (
        record.version === 1 &&
        isNonEmptyTx(record.tx) &&
        record.phase !== undefined &&
        VALID_OAUTH_BROKER_PHASES.includes(record.phase) &&
        typeof record.createdAtMs === 'number' &&
        Number.isFinite(record.createdAtMs)
    );
}

function parseSingleTx(parameters: URLSearchParams): string | null {
    const entries: Array<[string, string]> = [];
    parameters.forEach((value, key) => entries.push([key, value]));

    if (entries.length !== 1) {
        return null;
    }

    const [[key, tx]] = entries;
    if (key !== 'tx' || !isNonEmptyTx(tx)) {
        return null;
    }

    return tx;
}

function brokerBasePath(pathname: string): string | null {
    if (!pathname.endsWith(OAUTH_BROKER_ROUTE)) {
        return null;
    }

    return pathname.slice(0, -OAUTH_BROKER_ROUTE.length).replace(/\/$/, '');
}

function sanitizedBrokerUrl(basePath: string): string {
    return `${basePath}/#${OAUTH_BROKER_ROUTE}`;
}

/**
 * Keeps one opaque broker transaction in tab-local storage while the user signs in.
 * The transaction starts in `pending_login`, moves to `continuing` when the continuation
 * request begins, and is then either cleared on success or replaced by a terminal reason.
 * Every read validates the stored record and fails closed if it is malformed or stale.
 */
export class OAuthBrokerContinuation {
    private readonly storage: Storage;
    private readonly clock: Clock;
    private ownsContinuation = false;
    private volatileTerminalReason: OAuthBrokerTerminalReason | null = null;

    constructor(storage: Storage, clock: Clock = Date.now) {
        this.storage = storage;
        this.clock = clock;
    }

    public capture(tx: string): boolean {
        return this.captureTransaction(tx, false);
    }

    public captureFromIngress(tx: string): boolean {
        // A newly opened tab can begin with a copy of its opener's sessionStorage.
        // An explicit outer broker entry starts a new transaction in that tab;
        // callback paths still use exact matching and never call this operation.
        return this.captureTransaction(tx, true);
    }

    private captureTransaction(tx: string, replaceExisting: boolean): boolean {
        if (!isNonEmptyTx(tx)) {
            this.setTerminal('invalid_request');
            return false;
        }

        const existing = this.readRecord();
        if (existing && existing.tx !== tx) {
            if (!replaceExisting) {
                this.setTerminal('state_mismatch');
                return false;
            }
        }

        if (existing && existing.tx === tx) {
            this.removeTerminal();
            return true;
        }

        return this.writeRecord({
            version: 1,
            tx,
            phase: 'pending_login',
            createdAtMs: this.clock(),
        });
    }

    public isActive(): boolean {
        return this.readRecord() !== null;
    }

    /**
     * Distinguishes a failure discovered while validating the active record from a terminal
     * reason that was already in storage. `readRecord()` can create a terminal reason when it
     * finds invalid or stale state, so the before-and-after reads preserve that ownership signal.
     */
    public inspect(): OAuthBrokerInspection {
        const terminalReasonBeforeStateRead = this.getTerminalReason();
        const active = this.isActive();
        const terminalReasonAfterStateRead = this.getTerminalReason();
        const owned = active || (!terminalReasonBeforeStateRead && !!terminalReasonAfterStateRead);
        const terminalReason = !active && owned ? terminalReasonAfterStateRead : null;

        return {
            active,
            owned,
            terminalReason,
        };
    }

    public getTx(): string | null {
        return this.readRecord()?.tx || null;
    }

    public getResumePath(): string | null {
        return this.isActive() ? OAUTH_BROKER_ROUTE : null;
    }

    public beginOnce(): string | null {
        const record = this.readRecord();
        if (!record) {
            return null;
        }
        if (record.phase === 'continuing') {
            if (!this.ownsContinuation) {
                this.setTerminal('invalid_transaction');
            }
            return null;
        }

        const continuingRecord: OAuthBrokerRecord = { ...record, phase: 'continuing' };
        if (!this.writeRecord(continuingRecord)) {
            return null;
        }
        this.ownsContinuation = true;
        return record.tx;
    }

    public clear(): void {
        this.ownsContinuation = false;
        this.volatileTerminalReason = null;
        try {
            this.storage.removeItem(OAUTH_BROKER_STORAGE_KEY);
            this.storage.removeItem(OAUTH_BROKER_TERMINAL_KEY);
        } catch {
            // Storage failures are reported by subsequent reads without exposing stored values.
        }
    }

    public setTerminal(reason: OAuthBrokerTerminalReason): void {
        this.ownsContinuation = false;
        this.volatileTerminalReason = reason;
        try {
            this.storage.removeItem(OAUTH_BROKER_STORAGE_KEY);
            this.storage.setItem(OAUTH_BROKER_TERMINAL_KEY, reason);
        } catch {
            this.volatileTerminalReason = 'storage_unavailable';
        }
    }

    public getTerminalReason(): OAuthBrokerTerminalReason | null {
        try {
            const reason = this.storage.getItem(OAUTH_BROKER_TERMINAL_KEY);
            if (reason === null) {
                return this.volatileTerminalReason;
            }
            return isTerminalReason(reason) ? reason : 'unknown';
        } catch {
            return 'storage_unavailable';
        }
    }

    public buildCrossOriginUrl(origin: string): string | null {
        const tx = this.getTx();
        if (!tx) {
            return null;
        }

        try {
            const target = new URL(origin);
            if (target.protocol !== 'https:' || target.username || target.password) {
                return null;
            }
            return `${
                target.origin
            }${OAUTH_BROKER_ROUTE}#${OAUTH_BROKER_ROUTE}?tx=${encodeURIComponent(tx)}`;
        } catch {
            return null;
        }
    }

    private readRecord(): OAuthBrokerRecord | null {
        let serialized: string | null;
        try {
            serialized = this.storage.getItem(OAUTH_BROKER_STORAGE_KEY);
        } catch {
            this.setTerminal('storage_unavailable');
            return null;
        }

        if (serialized === null) {
            return null;
        }

        let record: unknown;
        try {
            record = JSON.parse(serialized);
        } catch {
            this.setTerminal('invalid_request');
            return null;
        }

        if (!isRecord(record)) {
            this.setTerminal('invalid_request');
            return null;
        }

        const ageMs = this.clock() - record.createdAtMs;
        if (ageMs < 0 || ageMs > OAUTH_BROKER_MAX_AGE_MS) {
            this.setTerminal('stale');
            return null;
        }
        return record;
    }

    private writeRecord(record: OAuthBrokerRecord): boolean {
        try {
            this.storage.setItem(OAUTH_BROKER_STORAGE_KEY, JSON.stringify(record));
        } catch {
            this.setTerminal('storage_unavailable');
            return false;
        }
        this.removeTerminal();
        return true;
    }

    private removeTerminal(): void {
        this.volatileTerminalReason = null;
        try {
            this.storage.removeItem(OAUTH_BROKER_TERMINAL_KEY);
        } catch {
            // The active record remains authoritative; a later read will fail closed if needed.
        }
    }
}

function isTerminalReason(reason: string | null): reason is OAuthBrokerTerminalReason {
    return reason !== null && TERMINAL_REASONS.some((terminalReason) => terminalReason === reason);
}

export function createOAuthBrokerContinuation(
    storage: Storage,
    clock: Clock = Date.now
): OAuthBrokerContinuation {
    return new OAuthBrokerContinuation(storage, clock);
}

function unavailable(): never {
    throw new Error('Session storage is unavailable');
}

const UNAVAILABLE_STORAGE: Storage = {
    get length(): number {
        return unavailable();
    },
    clear: unavailable,
    getItem: unavailable,
    key: unavailable,
    removeItem: unavailable,
    setItem: unavailable,
};

export function createBrowserOAuthBrokerContinuation(
    browser: Pick<Window, 'sessionStorage'>
): OAuthBrokerContinuation {
    try {
        return createOAuthBrokerContinuation(browser.sessionStorage);
    } catch {
        return createOAuthBrokerContinuation(UNAVAILABLE_STORAGE);
    }
}

export function normalizeOAuthBrokerIngress(
    browser: BrowserForIngress,
    clock: Clock = Date.now
): OAuthBrokerIngressResult {
    const basePath = brokerBasePath(browser.location.pathname);
    if (basePath === null) {
        return 'not_broker';
    }

    let continuation: OAuthBrokerContinuation;
    try {
        continuation = createOAuthBrokerContinuation(browser.sessionStorage, clock);
    } catch {
        browser.history.replaceState(null, '', sanitizedBrokerUrl(basePath));
        return 'storage_unavailable';
    }

    let tx: string | null = null;
    let hasBrokerInput = false;

    if (browser.location.search) {
        hasBrokerInput = true;
        tx = parseSingleTx(new URLSearchParams(browser.location.search));
    } else if (browser.location.hash.startsWith(`#${OAUTH_BROKER_ROUTE}?`)) {
        hasBrokerInput = true;
        tx = parseSingleTx(
            new URLSearchParams(browser.location.hash.slice(`#${OAUTH_BROKER_ROUTE}?`.length))
        );
    }

    let result: OAuthBrokerIngressResult;
    if (hasBrokerInput) {
        if (!tx) {
            continuation.setTerminal('invalid_request');
            result = 'invalid';
        } else {
            result = continuation.captureFromIngress(tx) ? 'captured' : 'invalid';
        }
    } else if (continuation.isActive() || continuation.getTerminalReason()) {
        result = 'resumed';
    } else {
        continuation.setTerminal('invalid_request');
        result = 'invalid';
    }

    browser.history.replaceState(null, '', sanitizedBrokerUrl(basePath));
    return result;
}
